import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Slider,
  Chip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Replay30Icon from '@mui/icons-material/Replay30';
import Replay10Icon from '@mui/icons-material/Replay10';
import Forward10Icon from '@mui/icons-material/Forward10';
import Forward30Icon from '@mui/icons-material/Forward30';
import SpeedIcon from '@mui/icons-material/Speed';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

const formatSeconds = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export interface TransportControlsBarProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
  onRelativeSeek: (seconds: number) => void;
  onStepFrame: (frames: number) => void;
  playbackRate: number;
  onSetPlaybackRate: (rate: number) => void;
  onJumpToPeriod: (period: number) => void;
  hasOvertime: boolean;
  isReelMode: boolean;
  currentReelIndex: number;
  totalReelCount: number;
  onPlayHighlightClip: (index: number) => void;
  onExitReelMode: () => void;
  currentTime?: number;
  duration?: number;
  onSeek?: (time: number) => void;
  isMuted?: boolean;
  onToggleMute?: () => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

export const TransportControlsBar: React.FC<TransportControlsBarProps> = ({
  isPlaying,
  onTogglePlay,
  onRelativeSeek,
  onStepFrame,
  playbackRate,
  onSetPlaybackRate,
  onJumpToPeriod,
  hasOvertime,
  isReelMode,
  currentReelIndex,
  totalReelCount,
  onPlayHighlightClip,
  onExitReelMode,
  currentTime,
  duration,
  onSeek,
  isMuted,
  onToggleMute,
  isFullscreen,
  onToggleFullscreen,
}) => {
  return (
    <Box
      sx={{
        bgcolor: '#111113',
        color: '#fff',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        px: { xs: 1, sm: 1.5 },
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
        gap: 1,
        overflowX: 'auto',
        flexShrink: 0,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {/* Left: Transport Seek & Step Buttons */}
      <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
        <Tooltip title="Rewind 30 seconds (Shift + Left Arrow)">
          <IconButton size="small" sx={{ color: '#fff', p: 0.75 }} onClick={() => onRelativeSeek(-30)} aria-label="Rewind 30 seconds">
            <Replay30Icon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Rewind 10 seconds (Left Arrow)">
          <IconButton size="small" sx={{ color: '#fff', p: 0.75 }} onClick={() => onRelativeSeek(-10)} aria-label="Rewind 10 seconds">
            <Replay10Icon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Step back 1 second">
          <Button
            size="small"
            variant="outlined"
            onClick={() => onRelativeSeek(-1)}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.25)',
              minWidth: 'auto',
              px: 0.8,
              py: 0.2,
              fontSize: '0.72rem',
              fontWeight: 600,
            }}
          >
            -1s
          </Button>
        </Tooltip>

        <IconButton
          size="medium"
          color="primary"
          onClick={onTogglePlay}
          sx={{
            bgcolor: 'primary.main',
            color: '#fff',
            mx: 0.5,
            p: 0.8,
            '&:hover': { bgcolor: 'primary.dark' },
          }}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
        </IconButton>

        <Tooltip title="Step forward 1 second">
          <Button
            size="small"
            variant="outlined"
            onClick={() => onRelativeSeek(1)}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.25)',
              minWidth: 'auto',
              px: 0.8,
              py: 0.2,
              fontSize: '0.72rem',
              fontWeight: 600,
            }}
          >
            +1s
          </Button>
        </Tooltip>

        <Tooltip title="Forward 10 seconds (Right Arrow)">
          <IconButton size="small" sx={{ color: '#fff', p: 0.75 }} onClick={() => onRelativeSeek(10)} aria-label="Forward 10 seconds">
            <Forward10Icon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Forward 30 seconds (Shift + Right Arrow)">
          <IconButton size="small" sx={{ color: '#fff', p: 0.75 }} onClick={() => onRelativeSeek(30)} aria-label="Forward 30 seconds">
            <Forward30Icon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.15)', my: 0.5, mx: 0.5 }} />

        <Tooltip title="Step backward 1 frame (comma key)">
          <Button
            size="small"
            variant="text"
            onClick={() => onStepFrame(-1)}
            sx={{
              color: 'rgba(255,255,255,0.85)',
              minWidth: 'auto',
              px: 0.6,
              py: 0.2,
              fontSize: '0.7rem',
              fontFamily: 'monospace',
            }}
          >
            -1f
          </Button>
        </Tooltip>

        <Tooltip title="Step forward 1 frame (period key)">
          <Button
            size="small"
            variant="text"
            onClick={() => onStepFrame(1)}
            sx={{
              color: 'rgba(255,255,255,0.85)',
              minWidth: 'auto',
              px: 0.6,
              py: 0.2,
              fontSize: '0.7rem',
              fontFamily: 'monospace',
            }}
          >
            +1f
          </Button>
        </Tooltip>

        {onToggleMute && (
          <>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.15)', my: 0.5, mx: 0.5 }} />
            <Tooltip title={isMuted ? 'Unmute audio (M)' : 'Mute audio (M)'}>
              <IconButton
                size="small"
                onClick={onToggleMute}
                aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
                sx={{
                  color: isMuted ? '#ef4444' : '#fff',
                  bgcolor: isMuted ? 'rgba(239, 68, 68, 0.15)' : 'transparent',
                  p: 0.6,
                  '&:hover': {
                    bgcolor: isMuted ? 'rgba(239, 68, 68, 0.25)' : 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                {isMuted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>

      {/* Center: Basic Seek Scrubber (Active when duration & onSeek are provided) */}
      {duration !== undefined && duration > 0 && onSeek && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 140, maxWidth: 360, mx: 1 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', minWidth: 32, fontFamily: 'monospace' }}>
            {formatSeconds(currentTime || 0)}
          </Typography>
          <Slider
            size="small"
            value={Math.min(duration, Math.max(0, currentTime || 0))}
            min={0}
            max={duration}
            step={0.5}
            onChange={(_, val) => onSeek(val as number)}
            aria-label="Seek video position"
            sx={{
              color: 'primary.main',
              '& .MuiSlider-thumb': { width: 10, height: 10 },
            }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', minWidth: 32, fontFamily: 'monospace' }}>
            {formatSeconds(duration)}
          </Typography>
        </Box>
      )}

      {/* Right: Playback Speed Slider, Period Jump Chips & Fullscreen */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexShrink={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SpeedIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.7)' }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 32, fontSize: '0.72rem' }}>
            {playbackRate.toFixed(2)}x
          </Typography>
          <Slider
            size="small"
            value={playbackRate}
            min={0.25}
            max={2.0}
            step={0.25}
            onChange={(_, val) => onSetPlaybackRate(val as number)}
            sx={{
              width: 70,
              color: 'primary.light',
              '& .MuiSlider-thumb': { width: 10, height: 10 },
            }}
          />
        </Box>

        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.15)', my: 0.5 }} />

        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)', mr: 0.5, fontSize: '0.72rem' }}>
            Period:
          </Typography>
          {[1, 2, 3].map((pNum) => (
            <Chip
              key={pNum}
              label={`P${pNum}`}
              size="small"
              onClick={() => onJumpToPeriod(pNum)}
              sx={{
                cursor: 'pointer',
                bgcolor: 'rgba(255,255,255,0.08)',
                color: '#fff',
                fontSize: '0.7rem',
                height: 22,
                '&:hover': { bgcolor: 'primary.main' },
              }}
            />
          ))}
          {hasOvertime && (
            <Chip
              label="OT"
              size="small"
              onClick={() => onJumpToPeriod(4)}
              sx={{
                cursor: 'pointer',
                bgcolor: 'rgba(255,255,255,0.08)',
                color: '#fff',
                fontSize: '0.7rem',
                height: 22,
                '&:hover': { bgcolor: 'primary.main' },
              }}
            />
          )}
        </Stack>

        {/* Highlight Reel Indicator (when active) */}
        {isReelMode && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              icon={<MovieFilterIcon sx={{ fontSize: 14 }} />}
              label={`Highlight ${currentReelIndex + 1} of ${totalReelCount}`}
              size="small"
              color="secondary"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
            <IconButton
              size="small"
              sx={{ color: '#fff', p: 0.25 }}
              disabled={currentReelIndex === 0}
              onClick={() => onPlayHighlightClip(currentReelIndex - 1)}
            >
              <SkipPreviousIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              sx={{ color: '#fff', p: 0.25 }}
              disabled={currentReelIndex === totalReelCount - 1}
              onClick={() => onPlayHighlightClip(currentReelIndex + 1)}
            >
              <SkipNextIcon fontSize="small" />
            </IconButton>
            <Button
              size="small"
              variant="text"
              onClick={onExitReelMode}
              sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', px: 0.5, minWidth: 'auto' }}
            >
              Full Game
            </Button>
          </Stack>
        )}

        {/* Fullscreen Button */}
        {onToggleFullscreen && (
          <>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.15)', my: 0.5 }} />
            <Tooltip title={isFullscreen ? 'Exit Fullscreen (Esc or F)' : 'Fullscreen (F)'}>
              <IconButton
                size="small"
                onClick={onToggleFullscreen}
                aria-label={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                sx={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  p: 0.6,
                  '&:hover': { color: '#fff', bgcolor: 'rgba(255, 255, 255, 0.1)' },
                }}
              >
                {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
    </Box>
  );
};
